/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ForwardRefExoticComponent, type Ref, type RefAttributes } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { RovoAnimatedLogo } from './components/RovoAnimatedLogo';
import { RovoColorLogo } from './components/RovoColorLogo';
import messages from './messages';
import type { AIButtonProps } from './types';

const atlaskitCssStyles = cssMap({
	pressableStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: '48px',
		width: '48px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.xxlarge'),
	},
	boxStyles: {
		width: '48px',
		height: '48px',
		flexShrink: 0,
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error - TS2322 - specific box shadow for rovo button treatment
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
		transition: 'transform 0.2s ease-in-out',
		willChange: 'transform',

		'&:hover': {
			transform: 'scale(1.125)',
			transition: 'transform 0.1s ease-in-out',
			cursor: 'pointer',
		},
		'&:active': {
			transform: 'scale(1.0625)',
		},
	},
	logoWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	unreadDot: {
		position: 'absolute',
		top: token('space.150'),
		right: token('space.150'),
		width: '9px',
		height: '9px',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.information.bold'),
		pointerEvents: 'none',
	},
	aiInboxUnreadDot: {
		position: 'absolute',
		top: token('space.150'),
		right: token('space.150'),
		width: '12px',
		height: '12px',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral.bold'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
	aiInboxUnreadDotFill: {
		width: '8px',
		height: '8px',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.information.bold'),
	},
});

export const AIButton: ForwardRefExoticComponent<
	Omit<AIButtonProps, 'ref'> & RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, AIButtonProps>(
	(
		{
			onClick,
			isDisabled,
			onMouseEnter,
			onMouseLeave,
			hasUnread,
			isAIInboxStatusIndicator,
			isAwaitingInput,
			proximityAnimationKey,
		},
		ref: Ref<HTMLButtonElement>,
	) => {
		const { formatMessage } = useIntl();
		const { colorMode } = useThemeObserver();
		const renderStatusIndicator = (testId: string) =>
			isAIInboxStatusIndicator ? (
				<Box xcss={atlaskitCssStyles.aiInboxUnreadDot} testId={testId} aria-hidden="true">
					<Box
						xcss={atlaskitCssStyles.aiInboxUnreadDotFill}
						testId="ai-button-status-indicator-fill"
					/>
				</Box>
			) : (
				<Box xcss={atlaskitCssStyles.unreadDot} testId={testId} aria-hidden="true" />
			);

		return (
			<Box xcss={atlaskitCssStyles.boxStyles} data-spotlight-target="rovo-ai-button">
				<Pressable
					testId="platform-ai-button"
					xcss={atlaskitCssStyles.pressableStyles}
					onClick={onClick}
					isDisabled={isDisabled}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onFocus={onMouseEnter}
					onBlur={onMouseLeave}
					ref={ref}
					interactionName="rovo-button-click"
				>
					<VisuallyHidden>{formatMessage(messages.rovoButton)}</VisuallyHidden>
					<Box xcss={atlaskitCssStyles.logoWrapper}>
						{proximityAnimationKey !== undefined ? (
							// `key` on the call site is the idiomatic React way to trigger
							// the SMIL animation replay: changing the key remounts the SVG,
							// browsers restart SMIL on insertion. RovoAnimatedLogo doesn't
							// need to know about this mechanism.
							<RovoAnimatedLogo
								key={proximityAnimationKey}
								colorMode={colorMode === 'dark' ? 'dark' : 'light'}
							/>
						) : (
							<RovoColorLogo colorMode={colorMode === 'dark' ? 'dark' : 'light'} />
						)}
					</Box>
				</Pressable>
				{/*
				 * Awaiting-input supersedes the plain unread indicator while retaining
				 * the same dot treatment.
				 * (TREX-1891 rule 1)
				 */}
				{isAwaitingInput
					? renderStatusIndicator('ai-button-awaiting-input-dot')
					: hasUnread && renderStatusIndicator('ai-button-unread-dot')}
			</Box>
		);
	},
);
